<template>
  <div class="payment-method" v-if="allSetup">
    <div class="payment-method-form">
      <h3>Add Payment Method</h3>
      <template v-if="state === 'error'">
        <div class="error-message">
          <h4>An error occurred</h4>
          <div v-if="error.message">{{ error.message }}</div>
          <div v-else>{{ error }}</div>
          <ux-button color="primary" @click="resetForm">
            Try again
            <ux-icon right icon="fa-undo" />
          </ux-button>
        </div>
      </template>
      <template v-else-if="state === 'success'">
        <div class="success-message">
          <h4>Payment method added successfully!</h4>
          <ux-button color="primary" @click="close">
            Done
            <ux-icon right icon="fa-check" />
          </ux-button>
        </div>
      </template>
      <div v-else>
        <form @submit.prevent.stop="submitPaymentMethod">
          <ux-form ref="form" v-model="dataModel" :fields="definedFields" />

          <ux-field-wrapper title="Card Number">
            <StripeElements
              v-slot="{ elements, instance }"
              ref="stripeElements"
              :stripe-key="stripeKey"
              :instance-options="instanceOptions"
              :elements-options="elementsOptions"
              :key="`stripe-${formKey}`"
            >
              <StripeElement
                ref="stripeCard"
                type="card"
                :elements="elements"
                :options="cardElementOptions"
              />
            </StripeElements>
            <div id="card-errors" class="error-text" v-if="cardError">
              {{ cardError }}
            </div>
          </ux-field-wrapper>

          <div class="form-actions">
            <ux-button
              type="button"
              color="secondary"
              @click="cancel"
              :disabled="processing"
            >
              Cancel
            </ux-button>
            <ux-button
              type="submit"
              color="primary"
              :disabled="invalid"
              :loading="processing"
            >
              {{ processing ? "Adding..." : "Add Payment Method" }}
            </ux-button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-else>Initialising secure payment form...</div>
</template>

<script>
import { StripeElements, StripeElement } from "vue-stripe-js";

const STATE_INIT = "init";
const STATE_READY = "ready";
const STATE_PROCESSING = "processing";
const STATE_SUCCESS = "success";
const STATE_ERROR = "error";

export default {
  name: "PaymentMethodForm",
  components: {
    StripeElements,
    StripeElement,
  },
  emits: ["close", "success", "error"],
  props: {
    sandbox: {
      type: Boolean,
    },
    formKey: {
      type: String,
      default() {
        return String(Math.random());
      },
    },
    stripeKey: {
      type: String,
      required: true,
    },
    integration: {
      type: String,
      required: true,
    },
    askEmail: {
      type: Boolean,
    },
    modelValue: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  async created() {
    const self = this;
    self.$sdk.utils
      .loadExternalScript("https://js.stripe.com/v3/")
      .then(function () {
        self.initialised = true;
        self.state = STATE_READY;
      });
  },
  computed: {
    allSetup() {
      return this.stripeKey && this.initialised;
    },
    activeUser() {
      return this.$sdk.app?.user || this.$sdk.auth.getCurrentUser();
    },
    integrationID() {
      return this.$sdk.utils.id(this.integration);
    },
    invalid() {
      return this.processing || !(this.dataModel.name || "").trim();
    },
    impersonatorName() {
      return [
        this.activeUser?.impersonatorFirstName,
        this.activeUser?.impersonatorLastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
    },
    actualName() {
      return [this.activeUser?.firstName, this.activeUser?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
    },
    impersonatorEmail() {
      return this.activeUser?.impersonatorEmail;
    },
    actualEmail() {
      return this.activeUser?.email;
    },
    defaultName() {
      return (
        this.dataModel.name || this.actualName || this.impersonatorName || ""
      );
    },
    defaultEmail() {
      return (
        this.dataModel.email || this.actualEmail || this.impersonatorEmail || ""
      );
    },
    definedFields() {
      const array = [];

      if (this.askEmail || !this.defaultEmail) {
        array.push({
          title: "Email Address",
          key: "email",
          minimum: 1,
          maximum: 1,
          type: "email",
          placeholder: "you@youremail.com",
          defaultValues: [this.defaultEmail],
        });
      }

      array.push({
        title: "Cardholder Name",
        key: "name",
        minimum: 1,
        maximum: 1,
        type: "string",
        placeholder: "John Citizen",
        defaultValues: [this.defaultName],
      });

      return array;
    },
  },
  data() {
    return {
      initialised: false,
      state: STATE_INIT,
      cardholderName: "",
      processing: false,
      error: null,
      cardError: null,
      dataModel: this.modelValue,
      instanceOptions: {},
      elementsOptions: {},
      cardElementOptions: {
        hidePostalCode: true,
        style: {
          base: {
            fontSize: "16px",
            color: "#424770",
            "::placeholder": {
              color: "#aab7c4",
            },
          },
          invalid: {
            color: "#9e2146",
          },
        },
      },
    };
  },
  watch: {
    stripeKey(key) {
      console.log("PMF: stripe key initialised", key);
    },
    initialised(i) {
      console.log("PMF initialised", i);
    },
    allSetup(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.resetForm();
        this.$nextTick(() => {
          this.setupCardErrorHandling();
        });
      }
    },
  },
  methods: {
    setupCardErrorHandling() {
      // Wait for Stripe elements to be ready
      this.$nextTick(() => {
        const stripeCard = this.$refs.stripeCard;
        if (stripeCard && stripeCard.stripeElement) {
          stripeCard.stripeElement.on("change", ({ error }) => {
            this.cardError = error ? error.message : null;
          });
        }
      });
    },

    async submitPaymentMethod() {
      const self = this;
      if (self.processing) return;

      self.processing = true;
      self.state = STATE_PROCESSING;
      self.cardError = null;

      try {
        // Step 1: Get setup intent from your backend
        const setupResponse = await self.startSetup();
        const { setupIntent, key, method } = setupResponse;

        const stripeElements = self.$refs.stripeElements;
        const stripeCard = self.$refs.stripeCard;

        if (!stripeElements || !stripeCard) {
          throw new Error("Stripe elements not ready");
        }

        let email = self.dataModel.email || self.defaultEmail || "";

        console.log("INSTANCE", {
          stripeElementsRef: stripeElements,
          stripeElementsInstance: stripeElements.instance,
          stripeCardElement: stripeCard.stripeElement,
        });

        // Step 2: Confirm the setup intent with Stripe
        const { error } = await stripeElements.instance.confirmCardSetup(
          setupIntent.client_secret,
          {
            payment_method: {
              card: stripeCard.stripeElement,
              billing_details: {
                name: self.dataModel.name || "",
                email,
              },
            },
          },
        );

        if (error) {
          console.log("Confirmation Error", error);
          throw error;
        }

        // Step 3: Confirm with your backend that setup was successful
        const confirmResponse = await self.confirmSetup(method._id);
        self.$emit("success", confirmResponse.method);
        self.state = STATE_SUCCESS;
      } catch (err) {
        console.error("Error saving payment method:", err);
        this.error = err;
        this.state = STATE_ERROR;
        this.$emit("error", err);
      } finally {
        this.processing = false;
      }
    },

    // Add these new methods:
    async startSetup() {
      let url = `/payment/${this.integrationID}/method/setup`;
      if (this.sandbox) {
        url = `/payment/${this.integrationID}/method/setup?sandbox=true`;
      }
      const response = await this.$sdk.api.get(url);
      return response.data;
    },

    async confirmSetup(methodId) {
      const response = await this.$sdk.api.post(
        `/payment/${this.integrationID}/method/confirm`,
        {
          method: methodId,
          sandbox: this.sandbox,
        },
      );
      return response.data;
    },

    resetForm() {
      this.state = STATE_READY;
      this.dataModel = {};
      this.processing = false;
      this.error = null;
      this.cardError = null;

      // Clear Stripe card element
      this.$nextTick(() => {
        const stripeCard = this.$refs.stripeCard;
        if (stripeCard && stripeCard.stripeElement) {
          stripeCard.stripeElement.clear();
        }
      });
    },

    cancel() {
      if (!this.processing) {
        this.close();
      }
    },

    close() {
      this.resetForm();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1em;
}

/* Stripe card element styling */
:deep(.StripeElement) {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  transition: border-color 0.15s ease;
}

:deep(.StripeElement:focus) {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

:deep(.StripeElement--invalid) {
  border-color: #dc3545;
}
</style>
